import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Ship, Factory, AlertTriangle, Columns3, BarChart3,
  Calendar, Clock, User, GripVertical, Search, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { TitleWithHelp } from '../components/ui/help-tooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

const COLUMNS = {
  todo: { title: 'A Fazer', gradient: 'from-slate-500 to-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
  in_production: { title: 'Em Producao', gradient: 'from-blue-500 to-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  finished: { title: 'Finalizado', gradient: 'from-emerald-500 to-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  delivered: { title: 'Entregue', gradient: 'from-violet-500 to-violet-600', bg: 'bg-violet-50', border: 'border-violet-200' }
};

const STATUS_COLORS = { green: 'bg-emerald-400', yellow: 'bg-amber-400', red: 'bg-red-400' };

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }); }
  catch { return '—'; }
};

const fmtDateFull = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('pt-BR'); }
  catch { return '—'; }
};

// ========== KANBAN CARD ==========
const KanbanCardItem = ({ card, provided, snapshot }) => (
  <div
    ref={provided.innerRef}
    {...provided.draggableProps}
    className={`group rounded-xl border bg-white transition-all mb-3 ${
      snapshot.isDragging ? 'shadow-2xl ring-2 ring-blue-400 rotate-1' : 'hover:shadow-md'
    }`}
  >
    <div className="p-3.5">
      <div className="flex items-start gap-2">
        <div {...provided.dragHandleProps} className="mt-1 cursor-grab active:cursor-grabbing opacity-30 group-hover:opacity-100 transition-opacity">
          <GripVertical className="h-4 w-4 text-slate-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-sm font-semibold text-slate-800 truncate">{card.project_name}</h4>
            <div className={`w-2.5 h-2.5 rounded-full ${STATUS_COLORS[card.status_color]} flex-shrink-0`} />
          </div>
          {card.client && (
            <p className="text-xs text-slate-500 flex items-center gap-1 mb-2">
              <User className="h-3 w-3" />{card.client}
            </p>
          )}
          <div className="flex items-center gap-1 text-xs text-slate-500 mb-2">
            <Ship className="h-3 w-3 text-blue-500" />
            <span className="truncate">{card.vessel_type}</span>
            {card.embarcacoes_count > 0 && (
              <Badge variant="outline" className="ml-auto text-[10px] h-4 px-1.5">{card.embarcacoes_count}x</Badge>
            )}
          </div>

          {/* Progress */}
          <div className="mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-[10px] text-slate-400">Progresso</span>
              <span className="text-[10px] font-bold text-slate-600">{(card.progress_percentage || 0).toFixed(0)}%</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  card.progress_percentage >= 100 ? 'bg-emerald-500' :
                  card.progress_percentage >= 50 ? 'bg-blue-500' :
                  card.progress_percentage > 0 ? 'bg-amber-400' : 'bg-slate-200'
                }`}
                style={{ width: `${Math.min(card.progress_percentage || 0, 100)}%` }}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-[10px] text-slate-400">
            <div className="flex items-center gap-2">
              {card.factory_name && (
                <span className="flex items-center gap-0.5"><Factory className="h-3 w-3" />{card.factory_name}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {card.delivery_date && (
                <span className="flex items-center gap-0.5"><Calendar className="h-3 w-3" />{fmtDate(card.delivery_date)}</span>
              )}
            </div>
          </div>

          {card.has_alerts && card.alert_message && (
            <div className="mt-2 bg-red-50 border border-red-100 rounded-lg px-2 py-1.5">
              <p className="text-[10px] text-red-600 flex items-center gap-1 font-medium">
                <AlertTriangle className="h-3 w-3" />{card.alert_message}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

// ========== GANTT CHART ==========
const GanttChart = ({ cards, projects }) => {
  const allProjects = useMemo(() => {
    const merged = (projects || []).map(p => {
      const card = cards.find(c => c.project_id === p.id);
      return { ...p, progress: card?.progress_percentage || p.progress || 0, status_color: card?.status_color || 'green' };
    });
    return merged.filter(p => p.start_date && p.delivery_date).sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  }, [cards, projects]);

  if (allProjects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-400">
        <BarChart3 className="h-12 w-12 mb-3 opacity-30" />
        <p>Nenhum projeto com datas definidas para exibir no Gantt</p>
      </div>
    );
  }

  const dates = allProjects.flatMap(p => [new Date(p.start_date), new Date(p.delivery_date)]);
  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));
  minDate.setDate(1);
  maxDate.setMonth(maxDate.getMonth() + 1);
  maxDate.setDate(0);
  const totalMs = maxDate - minDate;

  const months = [];
  const d = new Date(minDate);
  d.setDate(1);
  while (d <= maxDate) {
    const pct = ((d - minDate) / totalMs) * 100;
    months.push({ label: d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }), pct: Math.max(pct, 0) });
    d.setMonth(d.getMonth() + 1);
  }

  const today = new Date();
  const todayPct = ((today - minDate) / totalMs) * 100;
  const ROW_H = 44;
  const TIMELINE_W = Math.max(months.length * 100, 700);

  const STATUS_BAR = {
    nao_iniciado: { bg: 'bg-slate-400', fill: 'bg-slate-500', text: 'Nao iniciado' },
    em_producao: { bg: 'bg-blue-400', fill: 'bg-blue-600', text: 'Em producao' },
    em_atraso: { bg: 'bg-red-400', fill: 'bg-red-600', text: 'Em atraso' },
    concluido: { bg: 'bg-emerald-400', fill: 'bg-emerald-600', text: 'Concluido' },
  };

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="gantt-chart">
      {/* Header */}
      <div className="border-b bg-slate-50 px-4 py-2.5 flex items-center gap-3">
        <BarChart3 className="h-4 w-4 text-blue-600" />
        <span className="text-sm font-semibold text-slate-700">Timeline de Projetos</span>
        <Badge variant="outline" className="text-xs">{allProjects.length} projetos</Badge>
      </div>

      <div className="flex">
        {/* Fixed left panel - project names */}
        <div className="flex-shrink-0 w-56 border-r bg-slate-50/70 z-10">
          <div className="h-9 border-b bg-slate-100 px-3 flex items-center">
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Projeto</span>
          </div>
          {allProjects.map((proj, idx) => {
            const sts = STATUS_BAR[proj.status] || STATUS_BAR.nao_iniciado;
            return (
              <div key={proj.id} className={`flex items-center h-[${ROW_H}px] px-3 border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`} style={{ height: ROW_H }}>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-800 truncate">{proj.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className={`w-1.5 h-1.5 rounded-full ${sts.bg}`} />
                    <span className="text-[9px] text-slate-400">{fmtDate(proj.start_date)} <ChevronRight className="inline h-2 w-2" /> {fmtDate(proj.delivery_date)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Scrollable timeline */}
        <div className="flex-1 overflow-x-auto">
          <div style={{ minWidth: TIMELINE_W }}>
            {/* Month headers */}
            <div className="relative h-9 border-b bg-slate-100">
              {months.map((m, i) => (
                <div key={i} className="absolute top-0 h-full border-l border-slate-200/70 px-2 flex items-center" style={{ left: `${m.pct}%` }}>
                  <span className="text-[10px] font-semibold text-slate-500 uppercase whitespace-nowrap">{m.label}</span>
                </div>
              ))}
              {todayPct >= 0 && todayPct <= 100 && (
                <div className="absolute top-0 h-full w-0.5 bg-red-500 z-10" style={{ left: `${todayPct}%` }}>
                  <div className="absolute top-0 -left-3 bg-red-500 text-white text-[8px] px-1.5 py-0.5 rounded-b font-bold shadow-sm">HOJE</div>
                </div>
              )}
            </div>

            {/* Rows with bars */}
            {allProjects.map((proj, idx) => {
              const start = new Date(proj.start_date);
              const end = new Date(proj.delivery_date);
              const leftPct = Math.max(((start - minDate) / totalMs) * 100, 0);
              const widthPct = Math.max(((end - start) / totalMs) * 100, 2);
              const sts = STATUS_BAR[proj.status] || STATUS_BAR.nao_iniciado;

              return (
                <div key={proj.id} className={`relative flex items-center border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} style={{ height: ROW_H }}>
                  {months.map((m, i) => (
                    <div key={i} className="absolute top-0 h-full border-l border-slate-100/70" style={{ left: `${m.pct}%` }} />
                  ))}
                  {todayPct >= 0 && todayPct <= 100 && (
                    <div className="absolute top-0 h-full w-0.5 bg-red-100 z-[1]" style={{ left: `${todayPct}%` }} />
                  )}
                  <div
                    className="absolute h-8 rounded-lg flex items-center overflow-hidden z-[2] shadow-sm border border-white/50"
                    style={{ left: `${leftPct}%`, width: `${widthPct}%`, minWidth: '60px' }}
                    title={`${proj.name}\n${fmtDateFull(proj.start_date)} - ${fmtDateFull(proj.delivery_date)}\nProgresso: ${(proj.progress || 0).toFixed(0)}%`}
                  >
                    <div className={`absolute inset-0 ${sts.bg} opacity-30`} />
                    <div className={`absolute top-0 left-0 h-full ${sts.fill} opacity-70 transition-all`} style={{ width: `${proj.progress || 0}%` }} />
                    <div className="relative z-10 flex items-center gap-1 px-2 w-full min-w-0">
                      <span className="text-[10px] font-semibold text-slate-900 truncate">{proj.name}</span>
                      <span className="text-[9px] text-slate-600 ml-auto flex-shrink-0 font-bold">{(proj.progress || 0).toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="border-t bg-slate-50 px-4 py-2 flex items-center gap-5 flex-wrap">
        {Object.values(STATUS_BAR).map(s => (
          <div key={s.text} className="flex items-center gap-1.5">
            <div className={`w-4 h-2.5 rounded-sm ${s.fill}`} />
            <span className="text-[10px] text-slate-500">{s.text}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 ml-2 border-l pl-3">
          <div className="w-4 h-0.5 bg-red-500" />
          <span className="text-[10px] text-slate-500">Hoje</span>
        </div>
      </div>
    </div>
  );
};

// ========== MAIN KANBAN PAGE ==========
const Kanban = () => {
  const [cards, setCards] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('kanban');
  const [search, setSearch] = useState('');

  useEffect(() => {
    document.title = "Fluxo de Producao — NautilusOS";
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cardsRes, projRes] = await Promise.all([
        api.get('/kanban-cards'),
        api.get('/projects'),
      ]);
      setCards(cardsRes.data);
      setProjects(projRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId) return;

    try {
      await api.patch(`/kanban-cards/${draggableId}/move`, { column: destination.droppableId });
      setCards(prev => prev.map(card => card.id === draggableId ? { ...card, column: destination.droppableId } : card));
      toast.success('Card movido!');
    } catch (error) {
      toast.error('Erro ao mover card');
    }
  };

  const filteredCards = useMemo(() => {
    if (!search.trim()) return cards;
    const s = search.toLowerCase();
    return cards.filter(c =>
      c.project_name?.toLowerCase().includes(s) ||
      c.client?.toLowerCase().includes(s) ||
      c.vessel_type?.toLowerCase().includes(s)
    );
  }, [cards, search]);

  const getCardsByColumn = (colId) => filteredCards.filter(c => c.column === colId);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-pulse text-slate-400">Carregando...</div>
    </div>
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <TitleWithHelp
            title="Fluxo de Producao"
            description="Visao macro do fluxo de producao. Cada card representa um projeto."
            connections={['Projetos', 'Producao']}
          >
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Columns3 className="h-6 w-6 text-blue-600" />
              Fluxo de Producao
            </h1>
          </TitleWithHelp>
          <p className="text-sm text-slate-500 mt-1">{cards.length} projetos no fluxo</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar projeto..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 w-56"
              data-testid="kanban-search"
            />
          </div>

          {/* View Mode Toggle */}
          <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="view-toggle">
            <Button
              size="sm"
              variant={viewMode === 'kanban' ? 'default' : 'ghost'}
              className={`h-8 gap-1.5 text-xs ${viewMode === 'kanban' ? '' : 'text-slate-500'}`}
              onClick={() => setViewMode('kanban')}
            >
              <Columns3 className="h-3.5 w-3.5" />Kanban
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'gantt' ? 'default' : 'ghost'}
              className={`h-8 gap-1.5 text-xs ${viewMode === 'gantt' ? '' : 'text-slate-500'}`}
              onClick={() => setViewMode('gantt')}
            >
              <BarChart3 className="h-3.5 w-3.5" />Gantt
            </Button>
          </div>
        </div>
      </div>

      {/* KANBAN VIEW */}
      {viewMode === 'kanban' && (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {Object.entries(COLUMNS).map(([columnId, column]) => {
              const columnCards = getCardsByColumn(columnId);
              return (
                <div key={columnId} className="flex flex-col">
                  {/* Column Header */}
                  <div className={`bg-gradient-to-r ${column.gradient} rounded-t-xl px-4 py-3 flex items-center justify-between`}>
                    <h2 className="font-semibold text-sm text-white">{column.title}</h2>
                    <Badge className="bg-white/20 text-white border-0 text-xs">{columnCards.length}</Badge>
                  </div>

                  <Droppable droppableId={columnId}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`flex-1 p-3 min-h-[450px] rounded-b-xl border-2 border-t-0 transition-colors ${
                          snapshot.isDraggingOver ? `${column.bg} ${column.border}` : 'bg-slate-50/50 border-slate-100'
                        }`}
                      >
                        {columnCards.map((card, index) => (
                          <Draggable key={card.id} draggableId={card.id} index={index}>
                            {(prov, snap) => <KanbanCardItem card={card} provided={prov} snapshot={snap} />}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {columnCards.length === 0 && (
                          <div className="text-center text-slate-300 py-12">
                            <Ship className="h-8 w-8 mx-auto mb-2 opacity-30" />
                            <p className="text-xs">Nenhum projeto</p>
                          </div>
                        )}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      )}

      {/* GANTT VIEW */}
      {viewMode === 'gantt' && <GanttChart cards={cards} projects={projects} />}

      {/* Empty State */}
      {cards.length === 0 && (
        <div className="text-center py-16">
          <Ship className="h-16 w-16 mx-auto mb-4 text-slate-300" />
          <p className="text-lg text-slate-500">Nenhum projeto no fluxo.</p>
          <p className="text-sm text-slate-400 mt-1">Crie um novo projeto para iniciar o rastreamento.</p>
        </div>
      )}
    </div>
  );
};

export default Kanban;
